import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Shield,
  User,
  Crown,
  Search,
  Filter,
  UserCheck,
  UserX
} from 'lucide-react';
import { userAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const queryClient = useQueryClient();

  const { data: users, isLoading } = useQuery(
    ['super-admin-users', roleFilter],
    () => {
      console.log('Fetching users with role filter:', roleFilter);
      return userAPI.getAllUsers({ role: roleFilter });
    },
    {
      select: (response) => {
        console.log('Users response:', response.data?.users);
        return response.data?.users || [];
      },
    }
  );

  const deleteMutation = useMutation(
    (userId) => userAPI.deleteUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('super-admin-users');
        toast.success('User deleted successfully');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to delete user';
        toast.error(errorMessage);
      },
    }
  );

  const reactivateMutation = useMutation(
    (userId) => userAPI.reactivateUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('super-admin-users');
        toast.success('User reactivated successfully');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to reactivate user';
        toast.error(errorMessage);
      },
    }
  );

  const permanentDeleteMutation = useMutation(
    (userId) => userAPI.permanentlyDeleteUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('super-admin-users');
        toast.success('User permanently deleted successfully');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to permanently delete user';
        toast.error(errorMessage);
      },
    }
  );

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'ADMIN':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'MANAGER':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'EMPLOYEE':
        return <User className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-yellow-100 text-yellow-800';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800';
      case 'MANAGER':
        return 'bg-purple-100 text-purple-800';
      case 'EMPLOYEE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleName = (role) => (typeof role === 'object' && role !== null ? role.name : role);

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Client-side role filtering as fallback
    const matchesRole = roleFilter === '' || getRoleName(user.role) === roleFilter;
    
    return matchesSearch && matchesRole;
  }) : [];

  const handleDelete = (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      deleteMutation.mutate(userId);
    }
  };

  const handleReactivate = (userId, userName) => {
    if (window.confirm(`Are you sure you want to reactivate user "${userName}"?`)) {
      reactivateMutation.mutate(userId);
    }
  };

  const handlePermanentDelete = (userId, userName) => {
    if (window.confirm(`Are you sure you want to PERMANENTLY DELETE user "${userName}"? This action cannot be undone!`)) {
      permanentDeleteMutation.mutate(userId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header - stacks on mobile */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center"
      >
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm md:text-base text-gray-600 mt-0.5 md:mt-1">Manage system users and their roles</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowCreateModal(true)}
          className="w-full md:w-auto btn-primary flex items-center justify-center space-x-2 py-3 md:py-2 touch-manipulation"
        >
          <Plus className="w-4 h-4" />
          <span>Create User</span>
        </motion.button>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm p-4 md:p-6"
      >
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full py-2.5 md:py-2"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="form-input flex-1 sm:flex-initial py-2.5 md:py-2 min-w-0"
            >
              <option value="">All Roles</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="ADMIN">Admin</option>
              <option value="MANAGER">Manager</option>
              <option value="EMPLOYEE">Employee</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Users List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {filteredUsers?.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredUsers.map((user, index) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 md:p-6 hover:bg-gray-50"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-start gap-3 md:gap-4 min-w-0 flex-1">
                    {/* Avatar - hidden on mobile */}
                    <span className="hidden md:block flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </span>
                      </div>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className={`md:hidden px-2 py-0.5 text-xs font-medium rounded-full ${getRoleColor(getRoleName(user.role))}`}>
                          {getRoleName(user.role)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 truncate">{user.email}</p>
                      <p className="text-xs text-gray-400 mt-0.5 hidden md:block">@{user.username}</p>
                      <p className="text-xs text-gray-400 mt-0.5 md:hidden">
                        Created {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between md:justify-end gap-2 flex-shrink-0">
                    <div className="hidden md:flex items-center space-x-2">
                      {getRoleIcon(getRoleName(user.role))}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(getRoleName(user.role))}`}>
                        {getRoleName(user.role)}
                      </span>
                    </div>
                    <div className="hidden md:block text-sm text-gray-500">
                      Created {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setEditingUser(user)}
                        className="p-2.5 md:p-2 text-gray-400 hover:text-blue-600 transition-colors touch-manipulation rounded-lg"
                        title="Edit User"
                      >
                        <Edit className="w-5 h-5 md:w-4 md:h-4" />
                      </motion.button>
                      {getRoleName(user.role) !== 'SUPER_ADMIN' && (
                        <>
                          {user.isActive ? (
                            <motion.button
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDelete(user.id, `${user.firstName} ${user.lastName}`)}
                              disabled={deleteMutation.isLoading}
                              className="p-2.5 md:p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 touch-manipulation rounded-lg"
                              title="Delete User"
                            >
                              <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                            </motion.button>
                          ) : (
                            <div className="flex items-center gap-1">
                              <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleReactivate(user.id, `${user.firstName} ${user.lastName}`)}
                                disabled={reactivateMutation.isLoading}
                                className="p-2.5 md:p-2 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50 touch-manipulation rounded-lg"
                                title="Reactivate User"
                              >
                                <UserCheck className="w-5 h-5 md:w-4 md:h-4" />
                              </motion.button>
                              <motion.button
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handlePermanentDelete(user.id, `${user.firstName} ${user.lastName}`)}
                                disabled={permanentDeleteMutation.isLoading}
                                className="p-2.5 md:p-2 text-gray-400 hover:text-red-700 transition-colors disabled:opacity-50 touch-manipulation rounded-lg"
                                title="Permanently Delete User"
                              >
                                <UserX className="w-5 h-5 md:w-4 md:h-4" />
                              </motion.button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-6 md:p-8 text-center">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || roleFilter 
                ? 'Try adjusting your search or filter criteria.'
                : 'No users have been created yet.'
              }
            </p>
            {!searchTerm && !roleFilter && (
              <div className="mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  Create First User
                </motion.button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries('super-admin-users');
          }}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            queryClient.invalidateQueries('super-admin-users');
          }}
        />
      )}
    </div>
  );
};

// Create User Modal Component
const CreateUserModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'EMPLOYEE',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create Manager through dedicated endpoint; Admin via admin endpoint; others via employee endpoint
      if (formData.role === 'MANAGER') {
        await userAPI.createManager(formData);
      } else if (formData.role === 'ADMIN') {
        await userAPI.createAdmin(formData);
      } else {
        await userAPI.createEmployee(formData);
      }
      toast.success('User created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Create New User
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>
        <div className="p-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="form-input"
              >
                <option value="EMPLOYEE">Employee</option>
                <option value="ADMIN">Admin</option>
                <option value="MANAGER">Manager</option>
              </select>
            </div>
            
            <div>
              <label className="form-label">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Employee Code</label>
              <input
                type="text"
                value={formData.employeeCode}
                onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })}
                className="form-input"
                placeholder="Optional employee code / number"
              />
            </div>

           
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onClose, onSuccess }) => {
  const resolvedRole = typeof user.role === 'object' && user.role !== null ? user.role.name : user.role;
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    role: resolvedRole === 'SUPER_ADMIN' ? 'SUPER_ADMIN' : (resolvedRole || 'EMPLOYEE'),
    employeeCode: user.employeeCode || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await userAPI.updateUser(user.id, formData);
      toast.success('User updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit User</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="form-input"
                required
              />
            </div>
            
            <div>
              <label className="form-label">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="form-input"
                disabled={resolvedRole === 'SUPER_ADMIN'}
              >
                {resolvedRole === 'SUPER_ADMIN' && (
                  <option value="SUPER_ADMIN">Super Admin</option>
                )}
                <option value="ADMIN">Admin</option>
                <option value="MANAGER">Manager</option>
                <option value="EMPLOYEE">Employee</option>
              </select>
              {resolvedRole === 'SUPER_ADMIN' && (
                <p className="mt-1 text-xs text-gray-500">
                  Role cannot be changed for Super Admin accounts.
                </p>
              )}
            </div>

            <div>
              <label className="form-label">Employee Code</label>
              <input
                type="text"
                value={formData.employeeCode}
                onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })}
                className="form-input"
                placeholder="Optional employee code / number"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;

